import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useParams,
  useSearchParams,
  Link as RouterLink,
} from 'react-router-dom'
import axios from 'axios'
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Tooltip,
  Checkbox,
  IconButton,
  Rating as MuiRating,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LazyImage from '../../components/lazy-image'
import ContentContainer from '../../areas/layout/content-container'
import BannerImage from '../../components/banner/banner-image'
import {
  StyledBannerContent,
  StyledPoster as DetailPoster,
  StyledPosterFallback,
} from '../../components/Details/details-page-styles'
import showsApi from '../../client/shows-api'
import { SeasonNotFound, LoadError } from '../Status/status-page'
import { ShowDetailDTO } from '../../types/Show'
import CommentsSection from '../../components/Details/comments-section'
import { useMediaState } from '../../context/media-state-context'
import { useUserRatings } from '../../hooks/useUserRatings'
import { hideSpoilers } from '../../utils/spoiler-visibility'

type SeasonResponse = {
  showId: number
  showTitle: string
  seasonNumber: number
  name: string
  posterUrl?: string
  backdropUrl?: string
  watchProviders?: { name: string; logoUrl?: string; link?: string }[]
  overview?: string
  airDate?: string
  episodes: {
    id: number
    name: string
    overview?: string
    stillUrl?: string
    airDate?: string
    episodeNumber: number
    rating: number
    runtime?: number
  }[]
}

const PageWrapper = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
}))

const TitleBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
  maxWidth: 760,
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    textAlign: 'center',
  },
}))

const OverviewText = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  lineHeight: 1.6,
})

const ActionsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center',
  },
}))

const EpisodesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: theme.spacing(2.5),
}))

const EpisodeCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'highlighted',
})<{ highlighted?: boolean }>(({ theme, highlighted }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  ...(highlighted && {
    animation: 'episodeGlow 3s ease-out forwards',
  }),
  '@keyframes episodeGlow': {
    '0%': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}66`,
    },
    '100%': {
      borderColor: theme.palette.divider,
      boxShadow: 'none',
    },
  },
}))

const EpisodeImage = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 160,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
}))

const EpisodeContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  flexGrow: 1,
}))

const EpisodeTitle = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
})

const EpisodeOverview = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
})

const EpisodeMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  color: theme.palette.text.secondary,
  fontSize: 13,
}))

const EpisodeActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: 'auto',
}))

const Rating = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  color: theme.palette.green.main,
  fontWeight: 600,
  fontSize: 14,
}))

const HistoryButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
}))

// Prev season / back to show / next season. On mobile the row spans the full
// width and spreads out so the small text buttons don't clump together.
const SeasonNavRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  alignSelf: 'flex-start',
  [theme.breakpoints.down('md')]: {
    alignSelf: 'stretch',
    width: '100%',
    justifyContent: 'center',
    flexWrap: 'wrap',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
}))

// Rendered as a real router link (component={RouterLink}) so right-click works
const SeasonNavButton = styled(Button)<{
  component?: React.ElementType
  to?: string
}>(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    fontSize: 13,
    minWidth: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

const SeasonTitle = styled(Typography)({
  fontWeight: 700,
})

const GuestSpoilerRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}))

const GuestSpoilerButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 700,
  color: theme.palette.brand.main,
  padding: 0,
  minWidth: 0,
}))

// Wraps the still so the spoiler blur can't bleed past the card edges.
const SpoilerBlurBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'blurred',
})<{ blurred: boolean }>(({ blurred }) => ({
  height: '100%',
  ...(blurred && {
    filter: 'blur(16px)',
    transform: 'scale(1.08)',
  }),
}))

const ClearRatingIcon = styled(CloseRoundedIcon)({
  fontSize: 14,
})

const CommentsToggleButton = styled(Button)({
  alignSelf: 'flex-start',
  textTransform: 'none',
  padding: 0,
})

const SpoilerableOverview = styled(EpisodeOverview, {
  shouldForwardProp: (prop) => prop !== 'blurred',
})<{ blurred: boolean }>(({ blurred }) => ({
  lineHeight: 1.5,
  ...(blurred && {
    filter: 'blur(5px)',
    userSelect: 'none',
  }),
}))

const LoadingWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 48,
})

const SeasonPage = () => {
  const { id, seasonNumber } = useParams<{ id: string; seasonNumber: string }>()
  const [searchParams] = useSearchParams()
  const targetEpisode = Number(searchParams.get('episode')) || null
  const episodeRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const [season, setSeason] = useState<SeasonResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<number | null>(null)
  const [openComments, setOpenComments] = useState<Record<number, boolean>>({})
  const {
    watchedEpisodesSet,
    toggleEpisodeWatched,
    setShowEpisodesWatched,
    isEpisodePending,
    isSeasonPending,
    isAuthenticated,
    mediaState,
  } = useMediaState()
  // One GET on the show returns the show + all episode rating rows
  const { episodeRatings, rateEpisode } = useUserRatings(
    'Show',
    season?.showId,
    isAuthenticated,
  )

  const loadSeason = useCallback(async () => {
    if (!id || !seasonNumber) return
    setLoading(true)
    setError(null)
    setStatus(null)
    try {
      const seasonRes = await showsApi.getSeason(
        Number(id),
        Number(seasonNumber),
      )
      setSeason(seasonRes.data)
    } catch (err) {
      setStatus(axios.isAxiosError(err) ? (err.response?.status ?? null) : null)
      setError('Failed to load season')
    } finally {
      setLoading(false)
    }
  }, [id, seasonNumber])

  useEffect(() => {
    loadSeason()
  }, [loadSeason])

  // Scroll to and glow the episode targeted by ?episode= (from a comment link).
  useEffect(() => {
    if (!season || targetEpisode == null) return
    const node = episodeRefs.current[targetEpisode]
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [season, targetEpisode])

  // Regular season numbers of this show (Specials excluded) for prev/next nav.
  const [showSeasonNumbers, setShowSeasonNumbers] = useState<number[]>([])
  useEffect(() => {
    if (!id) return
    let cancelled = false
    showsApi
      .getById(Number(id))
      .then((res) => {
        if (cancelled) return
        const detail = res.data as ShowDetailDTO
        const regularSeasonNumbers = (detail.seasons ?? [])
          .map((season) => season.seasonNumber)
          .filter((seasonNum) => seasonNum >= 1)
          .sort((first, second) => first - second)
        setShowSeasonNumbers(regularSeasonNumbers)
      })
      .catch(() => setShowSeasonNumbers([]))
    return () => {
      cancelled = true
    }
  }, [id])

  const currentIndex = showSeasonNumbers.indexOf(Number(seasonNumber))
  const prevSeasonNumber =
    currentIndex > 0 ? showSeasonNumbers[currentIndex - 1] : null
  const nextSeasonNumber =
    currentIndex >= 0 && currentIndex < showSeasonNumbers.length - 1
      ? showSeasonNumbers[currentIndex + 1]
      : null

  const seasonTitle = useMemo(
    () => `${season?.showTitle || 'Show'} - ${season?.name || ''}`,
    [season?.name, season?.showTitle],
  )

  const watchedSet = useMemo(() => watchedEpisodesSet, [watchedEpisodesSet])

  // Spoiler protection: blur unwatched episodes of a show the user has started
  const spoilersHidden = isAuthenticated && hideSpoilers()
  // Logged-out visitors get everything blurred (they have no watch history to
  // scope the blur), with a one-click page-level reveal kept for the session.
  const [guestRevealed, setGuestRevealed] = useState(
    () => sessionStorage.getItem('guest-spoilers-revealed') === '1',
  )
  const guestBlur = !isAuthenticated && !guestRevealed
  const toggleGuestReveal = () => {
    const next = !guestRevealed
    sessionStorage.setItem('guest-spoilers-revealed', next ? '1' : '0')
    setGuestRevealed(next)
  }
  const hasStartedShow = useMemo(() => {
    if (!season) return false
    const entry = mediaState?.history.find((h) => h.tmdbId === season.showId)
    return (entry?.episodes?.length ?? 0) > 0
  }, [mediaState, season])
  const seasonWatchedCount = useMemo(
    () =>
      season ? season.episodes.filter((ep) => watchedSet.has(ep.id)).length : 0,
    [season, watchedSet],
  )
  const seasonAllWatched = season
    ? seasonWatchedCount === season.episodes.length
    : false
  const seasonPending = useMemo(
    () =>
      season && season.showId && season.seasonNumber
        ? isSeasonPending(season.showId, season.seasonNumber)
        : false,
    [isSeasonPending, season],
  )

  const markAll = async () => {
    if (!season) return
    await setShowEpisodesWatched(
      season.episodes.map((e) => e.id),
      {
        showId: season.showId,
        showTitle: season.showTitle,
        posterUrl: season.posterUrl,
        airDate: season.airDate,
        genres: [],
        episodes: season.episodes.map((e) => ({
          id: e.id,
          name: e.name,
          episodeNumber: e.episodeNumber,
          seasonNumber: season.seasonNumber,
          airDate: e.airDate,
        })),
      },
    )
  }

  const clearAll = async () => {
    if (!season) return
    await setShowEpisodesWatched([], {
      showId: season.showId,
      showTitle: season.showTitle,
      posterUrl: season.posterUrl,
      airDate: season.airDate,
      genres: [],
      episodes: season.episodes.map((e) => ({
        id: e.id,
        name: e.name,
        episodeNumber: e.episodeNumber,
        seasonNumber: season.seasonNumber,
        airDate: e.airDate,
      })),
    })
  }

  if (loading) {
    return (
      <ContentContainer>
        <LoadingWrapper>
          <CircularProgress />
        </LoadingWrapper>
      </ContentContainer>
    )
  }

  if (error || !season) {
    return status && status !== 404 ? (
      <LoadError onRetry={loadSeason} />
    ) : (
      <SeasonNotFound
        actions={[{ label: 'Go to Show', to: `/details/show/${id}` }]}
      />
    )
  }

  return (
    <>
      <BannerImage imageUrl={season.backdropUrl || season.posterUrl}>
        <ContentContainer>
          <StyledBannerContent>
            <DetailPoster>
              <LazyImage
                src={season.posterUrl}
                alt={season.name}
                fallback={
                  <StyledPosterFallback>{season.name}</StyledPosterFallback>
                }
              />
            </DetailPoster>

            <TitleBlock>
              <SeasonTitle variant='h4'>{seasonTitle}</SeasonTitle>
              {season.airDate && (
                <Typography variant='body2' color='text.secondary'>
                  First aired {season.airDate}
                </Typography>
              )}

              {season.overview && (
                <Tooltip title={season.overview}>
                  <OverviewText variant='body1' color='text.secondary'>
                    {season.overview}
                  </OverviewText>
                </Tooltip>
              )}

              {season.episodes.length > 0 ? (
                <ActionsRow>
                  <HistoryButton
                    variant='contained'
                    onClick={seasonAllWatched ? clearAll : markAll}
                    startIcon={<CheckCircleIcon />}
                    disabled={seasonPending || !isAuthenticated}
                  >
                    {seasonAllWatched
                      ? 'Remove all from watched'
                      : 'Add all to watched'}
                  </HistoryButton>
                </ActionsRow>
              ) : null}
              <SeasonNavRow>
                {prevSeasonNumber != null && (
                  <SeasonNavButton
                    variant='text'
                    component={RouterLink}
                    to={`/shows/${season.showId}/season/${prevSeasonNumber}`}
                  >
                    ← Season {prevSeasonNumber}
                  </SeasonNavButton>
                )}
                <SeasonNavButton
                  variant='text'
                  component={RouterLink}
                  to={`/details/show/${season.showId}`}
                >
                  Back to show
                </SeasonNavButton>
                {nextSeasonNumber != null && (
                  <SeasonNavButton
                    variant='text'
                    component={RouterLink}
                    to={`/shows/${season.showId}/season/${nextSeasonNumber}`}
                  >
                    Season {nextSeasonNumber} →
                  </SeasonNavButton>
                )}
              </SeasonNavRow>
            </TitleBlock>
          </StyledBannerContent>
        </ContentContainer>
      </BannerImage>

      <ContentContainer>
        <PageWrapper>
          {!isAuthenticated && season.episodes.length > 0 && (
            <GuestSpoilerRow>
              <Typography variant='body2' color='text.secondary'>
                {guestBlur
                  ? 'Episode images and overviews are hidden to avoid spoilers.'
                  : 'Spoilers are visible.'}
              </Typography>
              <GuestSpoilerButton
                variant='text'
                size='small'
                onClick={toggleGuestReveal}
              >
                {guestBlur ? 'Show episodes' : 'Hide spoilers'}
              </GuestSpoilerButton>
            </GuestSpoilerRow>
          )}
          <EpisodesGrid>
            {season.episodes.map((ep) => {
              const isSpoilerBlurred =
                guestBlur ||
                (spoilersHidden && hasStartedShow && !watchedSet.has(ep.id))

              return (
                <EpisodeCard
                  key={ep.id}
                  ref={(node: HTMLDivElement | null) => {
                    episodeRefs.current[ep.episodeNumber] = node
                  }}
                  highlighted={targetEpisode === ep.episodeNumber}
                >
                  <EpisodeImage>
                    <SpoilerBlurBox blurred={isSpoilerBlurred}>
                      <LazyImage
                        src={ep.stillUrl}
                        alt={ep.name}
                        fallback={<Typography padding={2}>No image</Typography>}
                      />
                    </SpoilerBlurBox>
                  </EpisodeImage>
                  <EpisodeContent>
                    <Tooltip title={ep.name}>
                      <EpisodeTitle variant='subtitle1' fontWeight={600}>
                        E{ep.episodeNumber}: {ep.name}
                      </EpisodeTitle>
                    </Tooltip>
                    {ep.overview && (
                      <Tooltip
                        title={
                          isSpoilerBlurred
                            ? isAuthenticated
                              ? 'Hidden to avoid spoilers. Mark as watched to reveal.'
                              : 'Hidden to avoid spoilers. Use "Show episodes" above to reveal.'
                            : ep.overview
                        }
                      >
                        <SpoilerableOverview
                          variant='body2'
                          color='text.secondary'
                          blurred={isSpoilerBlurred}
                        >
                          {ep.overview}
                        </SpoilerableOverview>
                      </Tooltip>
                    )}
                    <EpisodeMeta>
                      <span>
                        {ep.airDate && ep.runtime ? (
                          <React.Fragment>
                            {ep.airDate} • {ep.runtime} min
                          </React.Fragment>
                        ) : null}
                      </span>
                    </EpisodeMeta>
                    {isAuthenticated && (
                      <EpisodeActions>
                        {ep.rating ? (
                          <Rating>
                            <StarRoundedIcon fontSize='inherit' />
                            {ep.rating.toFixed(1)}
                          </Rating>
                        ) : (
                          <Typography variant='body2' color='text.secondary'>
                            Unrated
                          </Typography>
                        )}
                        <Tooltip
                          title={
                            watchedSet.has(ep.id)
                              ? 'Watched'
                              : 'Mark as watched'
                          }
                        >
                          <Box display='flex' alignItems='center' gap={1}>
                            <Checkbox
                              color='primary'
                              checked={watchedSet.has(ep.id)}
                              disabled={
                                isEpisodePending(ep.id) || seasonPending
                              }
                              onChange={() =>
                                toggleEpisodeWatched(
                                  {
                                    id: ep.id,
                                    name: ep.name,
                                    episodeNumber: ep.episodeNumber,
                                    seasonNumber: season.seasonNumber,
                                    airDate: ep.airDate,
                                  },
                                  {
                                    showId: season.showId,
                                    showTitle: season.showTitle,
                                    posterUrl: season.posterUrl,
                                    airDate: season.airDate,
                                    genres: [],
                                    episodes: season.episodes.map((e) => ({
                                      id: e.id,
                                      name: e.name,
                                      episodeNumber: e.episodeNumber,
                                      seasonNumber: season.seasonNumber,
                                      airDate: e.airDate,
                                    })),
                                  },
                                )
                              }
                            />
                          </Box>
                        </Tooltip>
                      </EpisodeActions>
                    )}
                    {isAuthenticated && (
                      <Box
                        display='flex'
                        alignItems='center'
                        gap={0.5}
                        flexWrap='wrap'
                      >
                        <Typography variant='caption' color='text.secondary'>
                          Your Rating
                        </Typography>
                        <Tooltip
                          title={
                            watchedSet.has(ep.id)
                              ? ''
                              : 'Mark the episode as watched to rate it'
                          }
                        >
                          <span>
                            <MuiRating
                              size='small'
                              max={10}
                              precision={0.5}
                              disabled={!watchedSet.has(ep.id)}
                              value={
                                episodeRatings.get(ep.id)?.ratings_Value ?? 0
                              }
                              onChange={(_, value) =>
                                rateEpisode(
                                  ep.id,
                                  season.seasonNumber,
                                  ep.episodeNumber,
                                  value,
                                )
                              }
                            />
                          </span>
                        </Tooltip>
                        {episodeRatings.get(ep.id) && (
                          <Tooltip title='Clear rating'>
                            <IconButton
                              size='small'
                              onClick={() =>
                                rateEpisode(
                                  ep.id,
                                  season.seasonNumber,
                                  ep.episodeNumber,
                                  null,
                                )
                              }
                            >
                              <ClearRatingIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    <CommentsToggleButton
                      variant='text'
                      size='small'
                      onClick={() =>
                        setOpenComments((prev) => ({
                          ...prev,
                          [ep.id]: !prev[ep.id],
                        }))
                      }
                    >
                      {openComments[ep.id] ? 'Hide comments' : 'Comments'}
                    </CommentsToggleButton>
                    {openComments[ep.id] && (
                      <CommentsSection
                        mediaType='Episode'
                        tmdbMediaId={season.showId}
                        tmdbEpisodeId={ep.id}
                        seasonNumber={season.seasonNumber}
                        episodeNumber={ep.episodeNumber}
                      />
                    )}
                  </EpisodeContent>
                </EpisodeCard>
              )
            })}
          </EpisodesGrid>
        </PageWrapper>
      </ContentContainer>
    </>
  )
}

export default SeasonPage
